import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ParameterPanel from './ParameterPanel';
import DXFViewport from './DXFViewport';
import MapViewport from './MapViewport';
import CalculationHistory from '../shared/CalculationHistory';
import { AlertCircle, Loader } from 'lucide-react';
import {
    buildCorridorWallLines,
    divisionPolylineToSegments,
    getEffectiveSubAreas,
    getManualModuleCount,
    getUsablePolygons,
    parseModuleSelectionId,
    patchGroupedModule,
    removeGroupAt,
    removeGroupedModule,
    replaceGroupedModule2,
    resultsWithGroupModules,
    shiftGroupBy,
    translateAllGroups,
    translateGrouped,
} from './sitePlannerGeometry';
import './SitePlanner.css';

const apiBase = (process.env.REACT_APP_API_ORIGIN ?? '') + '/api/site-planner';
const MAX_UNDO = 50;

async function readApiJson(response) {
    const text = await response.text();
    let data = null;
    if (text) {
        try {
            data = JSON.parse(text);
        } catch {
            const title = text.match(/<title>([^<]+)<\/title>/i)?.[1]?.trim();
            const status = response.status ? `HTTP ${response.status}` : 'the server';
            throw new Error(
                title
                    ? `The server returned a web page (${status}: ${title}) instead of data.`
                    : `The server returned a web page (${status}) instead of data.`,
            );
        }
    }
    if (!response.ok) {
        throw new Error(data?.error || data?.detail || `Request failed (HTTP ${response.status}).`);
    }
    return data;
}

const DEFAULT_M1 = {
    length: 22.0,
    width: 16.0,
    height: 2.5,
    gap_length: 2.0,
    gap_width: 2.0,
};

const DEFAULT_M2 = {
    length: 18.0,
    width: 4.0,
    height: 2.5,
    gap_length: 2.0,
    gap_width: 2.0,
};

const DEFAULT_M3 = {
    length: 8.0,
    width: 3.0,
    height: 3.0,
    gap_length: 2.0,
    gap_width: 2.0,
};

const DEFAULT_M4 = {
    length: 6.0,
    width: 2.5,
    height: 2.5,
    gap_length: 2.0,
    gap_width: 2.0,
};

const DEFAULT_GROUP_CONFIG = {
    m1_count: 1,
    m2_count: 4,
    gap: 6.0,
};

const DEFAULT_CAMPUS = {
    datahall_count: 4,
    dh_length: 16,
    dh_width: 16,
    corridor_width_c: 4,
    corridor_width_ch: 4,
    ehouse_rows: 2,
    ehouse_cols: 2,
    ehouse_length: 20,
    ehouse_width: 4,
    ehouse_gap: 2,
    field_gap: 6,
    campus_count: null,
    campus_gap: 6,
    central_enabled: false,
    central_length: 20,
    central_width: 20,
    wings: ['E', 'W'],
};

const INFRA_PALETTE_TILES = [
    { type: 'M3', label: 'M3 (GIS)', color: '#fbbf24', bg: 'rgba(251, 191, 36, 0.15)' },
    { type: 'M4', label: 'M4 (TX)', color: '#60a5fa', bg: 'rgba(96, 165, 250, 0.15)' },
];

export default function SitePlanner() {
    const historyRef = useRef(null);
    const undoStackRef = useRef([]);
    const [searchParams, setSearchParams] = useSearchParams();

    const [mode, setMode] = useState('dxf');
    const [boundary, setBoundary] = useState([]);
    const [setback, setSetback] = useState(4.0);
    const [origin, setOrigin] = useState(null);
    const [m1Params, setM1Params] = useState(DEFAULT_M1);
    const [m2Params, setM2Params] = useState(DEFAULT_M2);
    const [m3Params, setM3Params] = useState(DEFAULT_M3);
    const [m4Params, setM4Params] = useState(DEFAULT_M4);
    const [optimizationMode, setOptimizationMode] = useState('groups');
    const [groupConfig, setGroupConfig] = useState(DEFAULT_GROUP_CONFIG);
    const [campusConfig, setCampusConfig] = useState(DEFAULT_CAMPUS);
    const [layoutObjective, setLayoutObjective] = useState('max_quantity');
    const [maxGroups, setMaxGroups] = useState(null);
    const [clusterRadius, setClusterRadius] = useState(50);
    const [module3Positions, setModule3Positions] = useState([]);
    const [module4Positions, setModule4Positions] = useState([]);
    const [acceptedGroupIds, setAcceptedGroupIds] = useState([]);
    const [dxfData, setDxfData] = useState(null);
    const [dxfLayers, setDxfLayers] = useState(null);
    const [dxfUnits, setDxfUnits] = useState('m');
    const [dxfScale, setDxfScale] = useState(1.0);
    const [visibleLayers, setVisibleLayers] = useState([]);
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [m2Dirty, setM2Dirty] = useState(false);
    const [exporting, setExporting] = useState(false);

    const [manualMode, setManualMode] = useState(false);
    const [panelTab, setPanelTab] = useState('global');
    const [toolMode, setToolMode] = useState('split');
    const [cutLines, setCutLines] = useState([]);
    const [corridors, setCorridors] = useState([]);
    const [closedDivisions, setClosedDivisions] = useState([]);
    const [subAreas, setSubAreas] = useState([]);
    const [selectedAreaIds, setSelectedAreaIds] = useState([]);
    const [areaHatchConfigs, setAreaHatchConfigs] = useState({});
    const [hatchAngleStep, setHatchAngleStep] = useState(null);
    const [areaLayouts, setAreaLayouts] = useState({});
    const [placedModules, setPlacedModules] = useState([]);
    const [selectedCutLineId, setSelectedCutLineId] = useState(null);
    const [selectedModuleId, setSelectedModuleId] = useState(null);
    const [paletteDragType, setPaletteDragType] = useState(null);
    const [corridorWidth, setCorridorWidth] = useState(4.0);
    const subAreasSyncRequestedRef = useRef(false);

    const handleManualModeChange = useCallback((enabled) => {
        setManualMode(enabled);
        if (enabled) {
            setPanelTab('manual');
            setToolMode('split');
        }
    }, []);

    const buildInputData = useCallback(
        () => ({
            mode,
            boundary,
            setback,
            module1: m1Params,
            module2: m2Params,
            module3: m3Params,
            module4: m4Params,
            optimization_mode: optimizationMode,
            layout_objective: layoutObjective,
            group_config: groupConfig,
            campus_config: campusConfig,
            max_groups: maxGroups,
            cluster_radius: clusterRadius,
            module3_positions: module3Positions,
            module4_positions: module4Positions,
            accepted_group_ids: acceptedGroupIds,
            origin: mode === 'map' ? origin : null,
            dxf_units: mode === 'dxf' ? dxfUnits : null,
            dxf_scale: mode === 'dxf' ? dxfScale : null,
            manual_mode: manualMode,
            tool_mode: toolMode,
            cut_lines: cutLines,
            corridors,
            closed_divisions: closedDivisions,
            sub_areas: subAreas,
            selected_area_ids: selectedAreaIds,
            area_hatch_configs: areaHatchConfigs,
            hatch_angle_step: hatchAngleStep,
            area_layouts: areaLayouts,
            placed_modules: placedModules,
            selected_cut_line_id: selectedCutLineId,
            selected_module_id: selectedModuleId,
            corridor_width: corridorWidth,
        }),
        [
            mode, boundary, setback, m1Params, m2Params, m3Params, m4Params,
            optimizationMode, layoutObjective, groupConfig, campusConfig, maxGroups, clusterRadius,
            module3Positions, module4Positions, acceptedGroupIds,
            origin, dxfUnits, dxfScale,
            manualMode, toolMode, cutLines, corridors, closedDivisions, subAreas, selectedAreaIds, areaHatchConfigs, areaLayouts,
            hatchAngleStep, placedModules, selectedCutLineId, selectedModuleId, corridorWidth,
        ],
    );

    const pushUndoSnapshot = useCallback(() => {
        undoStackRef.current.push({
            boundary: boundary.map((p) => [...p]),
            origin: origin ? { ...origin } : null,
            results: results ? JSON.parse(JSON.stringify(results)) : null,
            cutLines: JSON.parse(JSON.stringify(cutLines)),
            corridors: JSON.parse(JSON.stringify(corridors)),
            closedDivisions: JSON.parse(JSON.stringify(closedDivisions)),
            subAreas: JSON.parse(JSON.stringify(subAreas)),
            selectedAreaIds: [...selectedAreaIds],
            areaHatchConfigs: JSON.parse(JSON.stringify(areaHatchConfigs)),
            hatchAngleStep,
            areaLayouts: JSON.parse(JSON.stringify(areaLayouts)),
            placedModules: JSON.parse(JSON.stringify(placedModules)),
            selectedCutLineId,
            selectedModuleId,
            module3Positions: JSON.parse(JSON.stringify(module3Positions)),
            module4Positions: JSON.parse(JSON.stringify(module4Positions)),
            acceptedGroupIds: [...acceptedGroupIds],
        });
        if (undoStackRef.current.length > MAX_UNDO) {
            undoStackRef.current.shift();
        }
    }, [boundary, origin, results, cutLines, corridors, closedDivisions, subAreas, selectedAreaIds, areaHatchConfigs, areaLayouts, hatchAngleStep, placedModules, selectedCutLineId, selectedModuleId, module3Positions, module4Positions, acceptedGroupIds]);

    const beginUndoableEdit = useCallback(() => {
        pushUndoSnapshot();
    }, [pushUndoSnapshot]);

    const handleUndo = useCallback(() => {
        const prev = undoStackRef.current.pop();
        if (!prev) return;
        setBoundary(prev.boundary);
        setOrigin(prev.origin);
        setResults(prev.results);
        setCutLines(prev.cutLines || []);
        setCorridors(prev.corridors || []);
        setClosedDivisions(prev.closedDivisions || []);
        setSubAreas(prev.subAreas || []);
        setSelectedAreaIds(prev.selectedAreaIds || []);
        setAreaHatchConfigs(prev.areaHatchConfigs || {});
        setHatchAngleStep(prev.hatchAngleStep || null);
        setAreaLayouts(prev.areaLayouts || {});
        setPlacedModules(prev.placedModules || []);
        setSelectedCutLineId(prev.selectedCutLineId || null);
        setSelectedModuleId(prev.selectedModuleId || null);
        setModule3Positions(prev.module3Positions || []);
        setModule4Positions(prev.module4Positions || []);
        setAcceptedGroupIds(prev.acceptedGroupIds || []);
        setError(null);
    }, []);

    const loadFromSaved = useCallback((inputData, resultsData) => {
        const inp = inputData || {};
        if (inp.mode) setMode(inp.mode);
        setBoundary(inp.boundary || []);
        if (inp.setback != null) setSetback(inp.setback);
        if (inp.module1) setM1Params(inp.module1);
        if (inp.module2) setM2Params(inp.module2);
        if (inp.module3) setM3Params(inp.module3);
        if (inp.module4) setM4Params(inp.module4);
        if (inp.optimization_mode) setOptimizationMode(inp.optimization_mode);
        if (inp.layout_objective) setLayoutObjective(inp.layout_objective);
        if (inp.group_config) setGroupConfig(inp.group_config);
        if (inp.campus_config) setCampusConfig({ ...DEFAULT_CAMPUS, ...inp.campus_config });
        if (inp.max_groups != null) setMaxGroups(inp.max_groups);
        if (inp.cluster_radius != null) setClusterRadius(inp.cluster_radius);
        setModule3Positions(inp.module3_positions || []);
        setModule4Positions(inp.module4_positions || []);
        setAcceptedGroupIds(inp.accepted_group_ids || []);
        if (inp.origin) setOrigin(inp.origin);
        if (inp.dxf_units) setDxfUnits(inp.dxf_units);
        if (inp.dxf_scale != null) setDxfScale(inp.dxf_scale);
        setManualMode(inp.manual_mode || false);
        setPanelTab(inp.manual_mode ? 'manual' : 'global');
        setToolMode(inp.tool_mode || 'split');
        setCutLines(inp.cut_lines || []);
        setCorridors(inp.corridors || []);
        setClosedDivisions(inp.closed_divisions || []);
        setSubAreas(inp.sub_areas || []);
        setSelectedAreaIds(inp.selected_area_ids || []);
        if (inp.area_hatch_configs) {
            setAreaHatchConfigs(inp.area_hatch_configs);
        } else if (inp.hatch_draft?.origin) {
            const legacy = {};
            (inp.selected_area_ids || ['area-0']).forEach((id) => {
                legacy[id] = {
                    origin: inp.hatch_draft.origin,
                    angle: inp.hatch_draft.angle,
                };
            });
            setAreaHatchConfigs(legacy);
        } else {
            setAreaHatchConfigs({});
        }
        setHatchAngleStep(inp.hatch_angle_step || null);
        setAreaLayouts(inp.area_layouts || {});
        setPlacedModules(inp.placed_modules || []);
        setSelectedCutLineId(inp.selected_cut_line_id || null);
        setSelectedModuleId(inp.selected_module_id || null);
        if (inp.corridor_width != null) setCorridorWidth(inp.corridor_width);
        setResults(resultsData || null);
        setDxfData(null);
        setDxfLayers(null);
        setVisibleLayers([]);
        setError(null);
        undoStackRef.current = [];
    }, []);

    useEffect(() => {
        const sid = searchParams.get('calcSession');
        if (!sid) return;
        let cancelled = false;
        axios
            .get(`/api/site-planner/saved-analyses/${sid}/`)
            .then((r) => {
                if (cancelled) return;
                const d = r.data;
                loadFromSaved(d.input_data, d.results_data);
                setSearchParams({}, { replace: true });
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [searchParams, setSearchParams, loadFromSaved]);

    const getSavePayload = useCallback(
        (isAuto) => ({
            input_data: buildInputData(),
            results_data: results,
            is_auto_saved: isAuto,
            planning_mode: mode,
            module1_count: results?.module1_count ?? null,
            usable_area_m2: results?.usable_area ?? null,
        }),
        [buildInputData, results, mode],
    );

    const toggleLayer = (layerName) => {
        setVisibleLayers((prev) =>
            prev.includes(layerName) ? prev.filter((l) => l !== layerName) : [...prev, layerName],
        );
    };

    const handleUploadDxf = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setLoading(true);
        setError(null);
        setResults(null);
        setBoundary([]);
        setCutLines([]);
        setCorridors([]);
        setClosedDivisions([]);
        setSubAreas([]);
        setSelectedAreaIds([]);
        setAreaHatchConfigs({});
        setHatchAngleStep(null);
        setAreaLayouts({});
        setPlacedModules([]);
        undoStackRef.current = [];

        const formData = new FormData();
        formData.append('file', file);
        formData.append('units', dxfUnits);
        formData.append('scale', String(dxfScale));

        try {
            const response = await fetch(`${apiBase}/parse-dxf/`, {
                method: 'POST',
                body: formData,
            });
            const data = await readApiJson(response);
            setDxfData(data);
            setDxfLayers(data.layers);
            setVisibleLayers(Object.keys(data.layers));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const refreshSubAreas = useCallback(
        async (lines, corridorSpecs = corridors, enclosureSpecs = closedDivisions) => {
            if (boundary.length < 3) {
                setError('Please draw a closed site boundary first (minimum 3 points).');
                return null;
            }
            setLoading(true);
            setError(null);
            try {
                const response = await fetch(`${apiBase}/split-areas/`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        boundary,
                        setback,
                        cut_lines: lines,
                        corridors: (corridorSpecs || []).map((c) => ({
                            centerline: c.centerline,
                            width: c.width,
                        })),
                        closed_divisions: (enclosureSpecs || []).map((d) => ({
                            boundary: d.boundary,
                        })),
                    }),
                });
                const data = await readApiJson(response);
                setSubAreas(data.sub_areas || []);
                if (!results?.usable_boundary && data.usable_boundary) {
                    setResults((prev) => ({
                        ...(prev || {}),
                        usable_boundary: data.usable_boundary,
                        usable_boundaries: data.usable_boundaries,
                    }));
                }
                return data;
            } catch (err) {
                setError(err.message);
                return null;
            } finally {
                setLoading(false);
            }
        },
        [boundary, setback, results, corridors, closedDivisions],
    );

    const splitPayloadLines = useCallback(
        (lines) => (lines || []).map((l) => [l.p1, l.p2]),
        [],
    );

    const handleDeleteSelection = useCallback(() => {
        const tag = document.activeElement?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        if (toolMode === 'corridor' || toolMode === 'split') return;

        if (selectedModuleId) {
            const parsed = parseModuleSelectionId(selectedModuleId);
            if (!parsed) return;
            beginUndoableEdit();
            if (parsed.type === 'placed') {
                setPlacedModules((prev) => prev.filter((m) => m.id !== parsed.id));
            } else if (parsed.type === 'hatch') {
                setAreaLayouts((prev) => {
                    const layout = prev[parsed.areaId];
                    if (!layout?.module1?.[parsed.index]) return prev;
                    const module1 = layout.module1.filter((_, index) => index !== parsed.index);
                    return {
                        ...prev,
                        [parsed.areaId]: {
                            ...layout,
                            module1,
                            module1_count: module1.length,
                        },
                    };
                });
            } else if (parsed.type === 'resultM1' || parsed.type === 'resultM2') {
                const kind = parsed.type === 'resultM2' ? 'module2' : 'module1';
                setResults((prev) => removeGroupedModule(prev, kind, parsed.index));
                if (parsed.type === 'resultM2') setM2Dirty(true);
            } else if (parsed.type === 'group') {
                setResults((prev) => removeGroupAt(prev, parsed.index));
            }
            setSelectedModuleId(null);
            return;
        }

        if (selectedCutLineId) {
            beginUndoableEdit();
            const newLines = cutLines.filter((line) => line.id !== selectedCutLineId);
            setCutLines(newLines);
            setSelectedCutLineId(null);
            refreshSubAreas(splitPayloadLines(newLines), corridors, closedDivisions);
        }
    }, [
        toolMode,
        selectedModuleId,
        selectedCutLineId,
        cutLines,
        corridors,
        closedDivisions,
        beginUndoableEdit,
        refreshSubAreas,
        splitPayloadLines,
    ]);

    useEffect(() => {
        const onKeyDown = (e) => {
            const tag = document.activeElement?.tagName;
            const typing = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
            if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
                if (typing) return;
                e.preventDefault();
                handleUndo();
                return;
            }
            if (e.key === 'Delete' || e.key === 'Backspace') {
                if (typing || e.repeat) return;
                if (!selectedModuleId && !selectedCutLineId) return;
                e.preventDefault();
                handleDeleteSelection();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [handleUndo, handleDeleteSelection, selectedModuleId, selectedCutLineId]);

    const effectiveSubAreas = useMemo(
        () => getEffectiveSubAreas(subAreas, results),
        [subAreas, results],
    );

    useEffect(() => {
        if (!manualMode) {
            subAreasSyncRequestedRef.current = false;
            return;
        }
        if (
            boundary.length >= 3 &&
            subAreas.length === 0 &&
            effectiveSubAreas.length === 0 &&
            !subAreasSyncRequestedRef.current
        ) {
            subAreasSyncRequestedRef.current = true;
            refreshSubAreas(splitPayloadLines(cutLines), corridors, closedDivisions);
        }
    }, [
        manualMode,
        boundary,
        subAreas,
        effectiveSubAreas,
        cutLines,
        corridors,
        closedDivisions,
        refreshSubAreas,
        splitPayloadLines,
    ]);

    const handleCommitDivisionPolyline = useCallback(
        async (points, closed) => {
            if (!points || points.length < 2) {
                setError('A division line needs at least two points.');
                return;
            }
            beginUndoableEdit();
            if (closed) {
                if (points.length < 3) {
                    setError('A closed cell needs at least three points.');
                    return;
                }
                const newDivision = {
                    id: `enclosure-${Date.now()}`,
                    boundary: points.map((p) => [...p]),
                };
                const newEnclosures = [...closedDivisions, newDivision];
                setClosedDivisions(newEnclosures);
                await refreshSubAreas(splitPayloadLines(cutLines), corridors, newEnclosures);
                return;
            }

            const ts = Date.now();
            const polylineId = `pline-${ts}`;
            const segLines = divisionPolylineToSegments(points).map((seg, index) => ({
                id: `cut-${ts}-${index}`,
                p1: seg[0],
                p2: seg[1],
                polylineId,
            }));
            const newLines = [...cutLines, ...segLines];
            setCutLines(newLines);
            await refreshSubAreas(splitPayloadLines(newLines), corridors, closedDivisions);
        },
        [
            cutLines,
            corridors,
            closedDivisions,
            beginUndoableEdit,
            refreshSubAreas,
            splitPayloadLines,
        ],
    );

    const getCorridorBorderPolygons = useCallback(() => {
        const polygons = [];
        if (boundary.length >= 3) polygons.push(boundary);
        polygons.push(...getUsablePolygons(results));
        (subAreas || []).forEach((area) => {
            if (area.boundary?.length >= 3) polygons.push(area.boundary);
        });
        return polygons;
    }, [boundary, results, subAreas]);

    const corridorWallLines = buildCorridorWallLines(corridors, getCorridorBorderPolygons());
    const allDisplayCutLines = [...cutLines, ...corridorWallLines];

    const handleCommitCorridor = useCallback(
        async (centerline) => {
            if (!centerline || centerline.length < 2) {
                setError('Draw at least two centerline points for the corridor.');
                return;
            }
            if (!(corridorWidth > 0)) {
                setError('Corridor width must be greater than zero.');
                return;
            }
            beginUndoableEdit();
            const newCorridor = {
                id: `corridor-${Date.now()}`,
                centerline,
                width: corridorWidth,
            };
            const newCorridors = [...corridors, newCorridor];
            setCorridors(newCorridors);
            await refreshSubAreas(splitPayloadLines(cutLines), newCorridors, closedDivisions);
        },
        [corridorWidth, corridors, closedDivisions, cutLines, beginUndoableEdit, refreshSubAreas, splitPayloadLines],
    );

    const handleUpdateCutLine = useCallback(
        async (lineId, p1, p2, skipUndo = false) => {
            if (!skipUndo) beginUndoableEdit();
            const newLines = cutLines.map((l) =>
                l.id === lineId ? { ...l, p1, p2 } : l,
            );
            setCutLines(newLines);
            await refreshSubAreas(splitPayloadLines(newLines), corridors, closedDivisions);
        },
        [cutLines, corridors, closedDivisions, beginUndoableEdit, refreshSubAreas, splitPayloadLines],
    );

    const handlePlaceModule = useCallback(
        (type, x, y, angle) => {
            beginUndoableEdit();
            const id = `pmod-${Date.now()}`;
            setPlacedModules((prev) => [
                ...prev,
                { id, type, x, y, angle: angle ?? 0 },
            ]);
            setSelectedModuleId(id);
            setSelectedCutLineId(null);
            setSelectedAreaIds([]);
        },
        [beginUndoableEdit],
    );

    const handleUpdatePlacedModule = useCallback(
        (moduleId, updates) => {
            setPlacedModules((prev) =>
                prev.map((m) => (m.id === moduleId ? { ...m, ...updates } : m)),
            );
        },
        [],
    );

    const handleUpdateHatchModule = useCallback((areaId, moduleIndex, updates) => {
        setAreaLayouts((prev) => {
            const layout = prev[areaId];
            if (!layout?.module1?.[moduleIndex]) return prev;
            const module1 = [...layout.module1];
            module1[moduleIndex] = { ...module1[moduleIndex], ...updates };
            return {
                ...prev,
                [areaId]: {
                    ...layout,
                    module1,
                    module1_count: module1.length,
                },
            };
        });
    }, []);

    const handleUpdateResultM1 = useCallback((moduleIndex, updates) => {
        setResults((prev) => patchGroupedModule(prev, 'module1', moduleIndex, updates));
    }, []);

    const handleUpdateResultM2 = useCallback((moduleIndex, updates) => {
        setResults((prev) => patchGroupedModule(prev, 'module2', moduleIndex, updates));
        setM2Dirty(true);
    }, []);

    const handleTranslateGroup = useCallback((groupIndex, dx, dy, snapshot) => {
        setResults((prev) => translateGrouped(prev, groupIndex, dx, dy, snapshot));
    }, []);

    const handleRefitCampus = useCallback(async (groupIndex, dx, dy, snapshot) => {
        if (!snapshot || !boundary || boundary.length < 3) return;
        const moved = shiftGroupBy(snapshot, dx, dy);
        try {
            const response = await fetch(`${apiBase}/refit-campus/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    boundary,
                    setback,
                    campus: campusConfig,
                    group: moved,
                }),
            });
            const data = await response.json();
            if (!response.ok || !data.success) return;
            setResults((prev) => {
                if (!prev?.groups?.[groupIndex]) return prev;
                const groups = prev.groups.map((group, index) => (
                    index === groupIndex ? { ...group, module2: data.module2 || [] } : group
                ));
                return resultsWithGroupModules(prev, groups);
            });
        } catch (err) {
            // The halls stay where they were dropped; eHouses keep the dragged pose.
        }
    }, [boundary, setback, campusConfig]);

    const handleTranslateCluster = useCallback((dx, dy, snapshots) => {
        setResults((prev) => translateAllGroups(prev, dx, dy, snapshots));
    }, []);

    const handleDeletePlacedModule = useCallback(
        (moduleId) => {
            beginUndoableEdit();
            setPlacedModules((prev) => prev.filter((m) => m.id !== moduleId));
            setSelectedModuleId((prev) => (prev === moduleId ? null : prev));
        },
        [beginUndoableEdit],
    );

    const clearSelection = useCallback(() => {
        setSelectedAreaIds([]);
        setSelectedCutLineId(null);
        setSelectedModuleId(null);
    }, []);

    const updateAreaHatchConfig = useCallback((areaId, patch) => {
        setAreaHatchConfigs((prev) => ({
            ...prev,
            [areaId]: { ...(prev[areaId] || {}), ...patch },
        }));
    }, []);

    const handleApplyHatch = useCallback(async () => {
        if (selectedAreaIds.length === 0) {
            setError('Select at least one sub-area to hatch.');
            return;
        }
        const configs = {};
        const missing = [];
        selectedAreaIds.forEach((areaId) => {
            const cfg = areaHatchConfigs[areaId];
            if (!cfg?.origin || cfg.angle == null) {
                missing.push(areaId);
                return;
            }
            configs[areaId] = {
                origin: cfg.origin,
                angle: cfg.angle,
                angle_ref: cfg.angleRef || null,
            };
        });
        if (missing.length > 0) {
            setError('Each selected area needs hatch origin and angle configured.');
            return;
        }
        const selectedAreas = effectiveSubAreas.filter((a) => selectedAreaIds.includes(a.id));
        if (selectedAreas.length === 0) {
            setError('Selected areas are no longer valid. Re-split or re-select.');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${apiBase}/pack-area-hatch/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    sub_areas: selectedAreas,
                    area_hatch_configs: configs,
                    module1: m1Params,
                    stagger: false,
                }),
            });
            const data = await readApiJson(response);
            beginUndoableEdit();
            setAreaLayouts((prev) => ({ ...prev, ...data.area_layouts }));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [selectedAreaIds, areaHatchConfigs, effectiveSubAreas, m1Params, beginUndoableEdit]);

    const handleRunOptimize = async (customM2Positions = null) => {
        if (boundary.length < 3) {
            setError('Please draw a closed site boundary first (minimum 3 points).');
            return;
        }
        if (
            optimizationMode === 'campus' &&
            campusConfig.central_enabled &&
            Number(campusConfig.datahall_count) > 0 &&
            (!campusConfig.wings || campusConfig.wings.length === 0)
        ) {
            setError('Select at least one wing when a central datahall is present.');
            return;
        }

        setLoading(true);
        setError(null);

        const m2Positions =
            customM2Positions ?? (m2Dirty && results?.module2 ? results.module2 : null);

        const campusM1 = {
            ...m1Params,
            length: Number(campusConfig.dh_length) || 16,
            width: Number(campusConfig.dh_width) || 16,
        };
        const campusM2 = {
            ...m2Params,
            length: Number(campusConfig.ehouse_length) || 20,
            width: Number(campusConfig.ehouse_width) || 4,
        };
        const payload = {
            boundary,
            setback,
            module1: optimizationMode === 'campus' ? campusM1 : m1Params,
            module2: optimizationMode === 'campus' ? campusM2 : m2Params,
            module3: m3Params,
            module4: m4Params,
            module2_positions: m2Positions,
            module3_positions: module3Positions,
            module4_positions: module4Positions,
            mode: optimizationMode,
            optimizer: optimizationMode === 'hatch' ? 'hatch' : 'current',
            group: groupConfig,
            max_groups: maxGroups,
            cluster_radius: clusterRadius,
            objective: layoutObjective,
            include_max_fill_reference: optimizationMode !== 'hatch' && optimizationMode !== 'campus',
            campus: optimizationMode === 'campus' ? campusConfig : undefined,
        };

        try {
            const response = await fetch(`${apiBase}/optimize-layout/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const data = await readApiJson(response);
            if (optimizationMode === 'campus') {
                setM1Params(campusM1);
                setM2Params(campusM2);
            }
            const groups = (data.groups || []).map((group) => ({
                ...group,
                accepted: acceptedGroupIds.includes(group.id),
            }));
            setResults({ ...data, groups, module3: module3Positions, module4: module4Positions });
            setAcceptedGroupIds([]);
            setM2Dirty(false);

            await refreshSubAreas(splitPayloadLines(cutLines), corridors, closedDivisions);

            historyRef.current?.saveAuto?.({
                input_data: buildInputData(),
                results_data: data,
                planning_mode: mode,
                module1_count: data.module1_count ?? null,
                usable_area_m2: data.usable_area ?? null,
            });
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleM2Commit = useCallback((updatedM2) => {
        setResults((prev) => replaceGroupedModule2(prev, updatedM2));
        setM2Dirty(true);
    }, []);

    const handleM3Commit = useCallback((updatedM3) => {
        setModule3Positions(updatedM3);
        setResults((prev) => (prev ? { ...prev, module3: updatedM3 } : prev));
    }, []);

    const handleM4Commit = useCallback((updatedM4) => {
        setModule4Positions(updatedM4);
        setResults((prev) => (prev ? { ...prev, module4: updatedM4 } : prev));
    }, []);

    const handleInfraPlace = useCallback(
        (type, x, y, angle) => {
            beginUndoableEdit();
            const mod = { x, y, angle: angle ?? 0 };
            if (type === 'M3') {
                setModule3Positions((prev) => [...prev, mod]);
            } else if (type === 'M4') {
                setModule4Positions((prev) => [...prev, mod]);
            }
        },
        [beginUndoableEdit],
    );

    const handleAcceptGroup = useCallback((groupId) => {
        setResults((prev) => {
            if (!prev?.groups) return prev;
            const groups = prev.groups.map((group) =>
                group.id === groupId ? { ...group, accepted: true } : group,
            );
            return { ...prev, groups };
        });
        setAcceptedGroupIds((prev) => (prev.includes(groupId) ? prev : [...prev, groupId]));
    }, []);

    const handleAcceptAllGroups = useCallback(() => {
        setResults((prev) => {
            if (!prev?.groups) return prev;
            const groups = prev.groups.map((group) => ({ ...group, accepted: true }));
            return { ...prev, groups };
        });
        setAcceptedGroupIds((prev) => {
            const ids = (results?.groups || []).map((g) => g.id);
            return [...new Set([...prev, ...ids])];
        });
    }, [results]);

    const handleExportDxf = useCallback(async () => {
        const hasBoundary = boundary.length >= 3;
        const hasDxf = dxfLayers && Object.keys(dxfLayers).length > 0;
        if (!hasBoundary && !hasDxf) {
            setError('Draw a closed boundary or import a DXF file before exporting.');
            return;
        }

        setExporting(true);
        setError(null);
        try {
            const response = await fetch(`${apiBase}/export-dxf/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    boundary,
                    results: results || null,
                    setback,
                    module1: m1Params,
                    module2: m2Params,
                    module3: m3Params,
                    module4: m4Params,
                    sub_areas: effectiveSubAreas,
                    area_layouts: areaLayouts,
                    placed_modules: placedModules,
                    cut_lines: allDisplayCutLines,
                    dxf_layers: dxfLayers,
                    visible_layers: visibleLayers,
                    units: mode === 'dxf' ? dxfUnits : 'm',
                    scale: mode === 'dxf' ? dxfScale : 1.0,
                }),
            });

            if (!response.ok) {
                let message = 'DXF export failed';
                try {
                    const data = await readApiJson(response);
                    message = data?.error || message;
                } catch (err) {
                    message = err.message || message;
                }
                throw new Error(message);
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'site_layout.dxf';
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(err.message);
        } finally {
            setExporting(false);
        }
    }, [
        boundary, results, setback, m1Params, m2Params, effectiveSubAreas, areaLayouts, placedModules, cutLines, corridors,
        allDisplayCutLines, dxfLayers, visibleLayers, mode, dxfUnits, dxfScale,
    ]);

    const canExportDxf =
        boundary.length >= 3 || (dxfLayers && Object.keys(dxfLayers).length > 0);

    const handleClearAll = () => {
        setBoundary([]);
        setResults(null);
        setError(null);
        setM2Dirty(false);
        setCutLines([]);
        setCorridors([]);
        setClosedDivisions([]);
        setSubAreas([]);
        setSelectedAreaIds([]);
        setAreaHatchConfigs({});
        setHatchAngleStep(null);
        setAreaLayouts({});
        setPlacedModules([]);
        setModule3Positions([]);
        setModule4Positions([]);
        setAcceptedGroupIds([]);
        setSelectedCutLineId(null);
        setSelectedModuleId(null);
        setManualMode(false);
        setPanelTab('global');
        setToolMode('split');
        undoStackRef.current = [];
        if (mode === 'map') {
            setOrigin(null);
        }
    };

    const handleSetMode = (newMode) => {
        setMode(newMode);
        handleClearAll();
        setDxfData(null);
        setDxfLayers(null);
        setVisibleLayers([]);
    };

  const manualModuleCount = getManualModuleCount(areaLayouts);

    const viewportProps = {
        boundary,
        setBoundary,
        setback,
        results,
        m1Params,
        m2Params,
        m3Params,
        m4Params,
        module3Positions,
        module4Positions,
        beginUndoableEdit,
        onM2Commit: handleM2Commit,
        onM3Commit: handleM3Commit,
        onM4Commit: handleM4Commit,
        onInfraPlace: handleInfraPlace,
        onAcceptGroup: handleAcceptGroup,
        manualMode,
        toolMode,
        cutLines,
        corridorWallLines,
        subAreas: effectiveSubAreas,
        hasSplitSubAreas: subAreas.length > 0,
        selectedAreaIds,
        setSelectedAreaIds,
        areaHatchConfigs,
        setAreaHatchConfigs,
        updateAreaHatchConfig,
        hatchAngleStep,
        setHatchAngleStep,
        setToolMode,
        areaLayouts,
        placedModules,
        selectedCutLineId,
        setSelectedCutLineId,
        selectedModuleId,
        setSelectedModuleId,
        onCommitDivisionPolyline: handleCommitDivisionPolyline,
        onCommitCorridor: handleCommitCorridor,
        corridorWidth,
        onUpdateCutLine: handleUpdateCutLine,
        onPlaceModule: handlePlaceModule,
        onUpdatePlacedModule: handleUpdatePlacedModule,
        onUpdateHatchModule: handleUpdateHatchModule,
        onUpdateResultM1: handleUpdateResultM1,
        onUpdateResultM2: handleUpdateResultM2,
        onTranslateGroup: handleTranslateGroup,
        onTranslateCluster: handleTranslateCluster,
        onRefitCampus: handleRefitCampus,
        onDeletePlacedModule: handleDeletePlacedModule,
        clearSelection,
        onManualModeChange: handleManualModeChange,
        paletteDragType,
        setPaletteDragType,
        setError,
    };

    return (
        <div className="sp-studio">
            <ParameterPanel
                mode={mode}
                setMode={handleSetMode}
                setback={setback}
                setSetback={setSetback}
                m1Params={m1Params}
                setM1Params={setM1Params}
                m2Params={m2Params}
                setM2Params={setM2Params}
                m3Params={m3Params}
                setM3Params={setM3Params}
                m4Params={m4Params}
                setM4Params={setM4Params}
                optimizationMode={optimizationMode}
                setOptimizationMode={setOptimizationMode}
                groupConfig={groupConfig}
                setGroupConfig={setGroupConfig}
                campusConfig={campusConfig}
                setCampusConfig={setCampusConfig}
                layoutObjective={layoutObjective}
                setLayoutObjective={setLayoutObjective}
                maxGroups={maxGroups}
                setMaxGroups={setMaxGroups}
                clusterRadius={clusterRadius}
                setClusterRadius={setClusterRadius}
                onAcceptAllGroups={handleAcceptAllGroups}
                infraPaletteTiles={INFRA_PALETTE_TILES}
                dxfLayers={dxfLayers}
                visibleLayers={visibleLayers}
                toggleLayer={toggleLayer}
                onRunOptimize={() => handleRunOptimize()}
                onClearAll={handleClearAll}
                results={results}
                onUploadDxf={handleUploadDxf}
                dxfUnits={dxfUnits}
                setDxfUnits={setDxfUnits}
                dxfScale={dxfScale}
                setDxfScale={setDxfScale}
                panelTab={panelTab}
                setPanelTab={setPanelTab}
                manualMode={manualMode}
                setManualMode={handleManualModeChange}
                toolMode={toolMode}
                setToolMode={setToolMode}
                corridorWidth={corridorWidth}
                setCorridorWidth={setCorridorWidth}
                subAreas={effectiveSubAreas}
                selectedAreaIds={selectedAreaIds}
                setSelectedAreaIds={setSelectedAreaIds}
                areaHatchConfigs={areaHatchConfigs}
                updateAreaHatchConfig={updateAreaHatchConfig}
                hatchAngleStep={hatchAngleStep}
                setHatchAngleStep={setHatchAngleStep}
                areaLayouts={areaLayouts}
                placedModules={placedModules}
                manualModuleCount={manualModuleCount + placedModules.length}
                m2Dirty={m2Dirty}
                onApplyHatch={handleApplyHatch}
                onExportDxf={handleExportDxf}
                exportDisabled={exporting || loading}
                canExportDxf={canExportDxf}
                onPaletteDragStart={setPaletteDragType}
                paletteDragType={paletteDragType}
                historySlot={
                    <CalculationHistory
                        ref={historyRef}
                        apiEndpoint="/api/site-planner/saved-analyses"
                        getSavePayload={getSavePayload}
                        appLabel="Site planner"
                        onLoad={({ input_data, results_data }) => loadFromSaved(input_data, results_data)}
                        renderSummary={(item) =>
                            [
                                item.planning_mode ? item.planning_mode.toUpperCase() : null,
                                item.module1_count != null ? `${item.module1_count} modules` : null,
                                item.usable_area_m2 != null ? `${Math.round(item.usable_area_m2)} m²` : null,
                            ]
                                .filter(Boolean)
                                .join(' · ')
                        }
                    />
                }
            />

            <div className="sp-workspace" style={{ flex: 1, position: 'relative' }}>
                {loading && (
                    <div className="sp-loader-overlay">
                        <Loader className="sp-spin mb-4" size={40} />
                        <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>
                            Processing layout geometry...
                        </span>
                    </div>
                )}

                {error && (
                    <div className="sp-error-banner">
                        <AlertCircle size={20} style={{ flexShrink: 0 }} />
                        <span>{error}</span>
                        <button type="button" onClick={() => setError(null)} className="sp-error-close">
                            ✕
                        </button>
                    </div>
                )}

                {mode === 'dxf' ? (
                    <DXFViewport
                        {...viewportProps}
                        dxfData={dxfData}
                        visibleLayers={visibleLayers}
                    />
                ) : (
                    <MapViewport
                        {...viewportProps}
                        origin={origin}
                        setOrigin={setOrigin}
                    />
                )}
            </div>
        </div>
    );
}
